import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  ChevronLeft, ChevronRight, Monitor, Cpu, MemoryStick, HardDrive,
  Package, Wrench, Printer, Shield, FolderOpen, UserCog, Activity,
  Network, Calendar, Hash, User, Building2, ArrowLeft, Edit, Trash2,
  ChevronUp, ChevronDown, ChevronsUpDown, Search, AlertTriangle,
  // Additional icons for detail fields
  Box, Tag, Globe, Wifi, Factory, CircuitBoard, Clock, DollarSign,
  CheckCircle, XCircle, Gauge, AlertCircle
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';

// Helper function to parse date string safely (handles "YYYY-MM-DD HH:mm:ss" and ISO formats)
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  // Replace space with 'T' for ISO format compatibility across all browsers
  const normalized = String(dateStr).replace(' ', 'T');
  const parsed = new Date(normalized);
  // Check if date is valid
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Helper function to determine agent status based on last report date
// Unified with /config/agentes rules:
// - Activo: reportó HOY (desde 00:00)
// - Advertencia: >24h sin reportar pero <7 días
// - Crítico: >7 días sin reportar
const getAgentStatus = (agente) => {
  if (!agente) return { status: 'not_installed', label: 'No instalado', color: 'yellow', icon: 'warning' };
  
  if (!agente.ultimo_reporte) return { status: 'unknown', label: 'Sin datos de reporte', color: 'gray', icon: 'unknown' };
  
  const lastReport = parseDate(agente.ultimo_reporte);
  if (!lastReport) return { status: 'unknown', label: 'Fecha inválida', color: 'gray', icon: 'unknown' };
  
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  
  if (lastReport >= todayStart) {
    return { status: 'active', label: 'Activo', color: 'green', icon: 'check' };
  } else if (lastReport >= weekAgo) {
    const diffHours = Math.floor((now - lastReport) / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const label = diffDays > 0 ? `Advertencia (${diffDays}d)` : `Advertencia (${diffHours}h)`;
    return { status: 'warning', label, color: 'yellow', icon: 'warning' };
  } else {
    const diffDays = Math.floor((now - lastReport) / (1000 * 60 * 60 * 24));
    return { status: 'critical', label: `Crítico (${diffDays}d)`, color: 'red', icon: 'error' };
  }
};

// Helper function to get OS display name from osversion
const getOSDisplayName = (osversion, osId) => {
  if (!osversion) return osId ? `OS ID: ${osId}` : '-';
  
  // Parse Windows version from osversion (e.g., "10.0.26200")
  const version = osversion.split('.').slice(0, 2).join('.');
  const build = parseInt(osversion.split('.')[2] || '0');
  
  // Windows version mapping based on build numbers
  if (version === '10.0') {
    if (build >= 22000) {
      // Windows 11
      if (build >= 26100) return 'Windows 11 24H2';
      if (build >= 22631) return 'Windows 11 23H2';
      if (build >= 22621) return 'Windows 11 22H2';
      return 'Windows 11';
    } else {
      // Windows 10
      if (build >= 19045) return 'Windows 10 22H2';
      if (build >= 19044) return 'Windows 10 21H2';
      if (build >= 19043) return 'Windows 10 21H1';
      if (build >= 19042) return 'Windows 10 20H2';
      if (build >= 19041) return 'Windows 10 2004';
      return 'Windows 10';
    }
  }
  if (version === '6.3') return 'Windows 8.1';
  if (version === '6.2') return 'Windows 8';
  if (version === '6.1') return 'Windows 7';
  
  return `Windows (${osversion})`;
};

const WorkstationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('assets', 'workstations');
  const [loading, setLoading] = useState(true);
  const [workstation, setWorkstation] = useState(null);
  const [hardware, setHardware] = useState(null);
  const [agente, setAgente] = useState(null);
  
  // Tab data
  const [software, setSoftware] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [softwareSearch, setSoftwareSearch] = useState('');
  const [softwareSort, setSoftwareSort] = useState({ field: 'name', dir: 'asc' });
  
  const [parches, setParches] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [parchesSearch, setParchesSearch] = useState('');
  const [parchesSort, setParchesSort] = useState({ field: 'fecha_instalacion', dir: 'desc' });
  
  const [impresoras, setImpresoras] = useState([]);
  const [impresorasSearch, setImpresorasSearch] = useState('');
  const [impresorasSort, setImpresorasSort] = useState({ field: 'printer', dir: 'asc' });
  
  const [seguridad, setSeguridad] = useState([]);
  const [seguridadFilter, setSeguridadFilter] = useState('all');
  
  const [shares, setShares] = useState([]);
  const [localadmin, setLocaladmin] = useState([]);
  
  const [activeTab, setActiveTab] = useState('general');
  
  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Sort handler factory
  const createSortHandler = (setter) => (field) => {
    setter(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field, sortConfig }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Generic sort function
  const sortData = (data, sortConfig) => {
    if (!data || !Array.isArray(data)) return [];
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.field] || '';
      let bVal = b[sortConfig.field] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filtered data getters
  const getFilteredImpresoras = () => {
    let filtered = impresoras.filter(imp => 
      !impresorasSearch || 
      imp.printer?.toLowerCase().includes(impresorasSearch.toLowerCase()) ||
      imp.drivername?.toLowerCase().includes(impresorasSearch.toLowerCase())
    );
    return sortData(filtered, impresorasSort);
  };

  const getFilteredSeguridad = () => {
    if (seguridadFilter === 'all') return seguridad;
    return seguridad.filter(s => s.tipo_seguridad?.toLowerCase() === seguridadFilter.toLowerCase());
  };

  useEffect(() => {
    fetchWorkstation();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'software') fetchSoftware();
    if (activeTab === 'parches') fetchParches();
    if (activeTab === 'impresoras') fetchImpresoras();
    if (activeTab === 'seguridad') fetchSeguridad();
    if (activeTab === 'shares') fetchShares();
    if (activeTab === 'localadmin') fetchLocaladmin();
  }, [activeTab, software.page, parches.page, softwareSearch, parchesSearch, softwareSort, parchesSort]);

  const fetchWorkstation = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/workstations/${id}`);
      setWorkstation(response.data.workstation);
      setHardware(response.data.hardware);
      setAgente(response.data.agente);
    } catch (error) {
      toast.error('Error al cargar workstation');
    } finally {
      setLoading(false);
    }
  };

  const fetchSoftware = async () => {
    try {
      const params = new URLSearchParams({ 
        page: software.page, 
        page_size: 25,
        sort_by: softwareSort.field,
        sort_dir: softwareSort.dir
      });
      if (softwareSearch) params.append('search', softwareSearch);
      const response = await api.get(`/assets/workstations/${id}/software?${params}`);
      setSoftware(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching software:', error);
    }
  };

  const fetchParches = async () => {
    try {
      const params = new URLSearchParams({ 
        page: parches.page, 
        page_size: 25,
        sort_by: parchesSort.field,
        sort_dir: parchesSort.dir
      });
      if (parchesSearch) params.append('search', parchesSearch);
      const response = await api.get(`/assets/workstations/${id}/parches?${params}`);
      setParches(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching parches:', error);
    }
  };

  const fetchImpresoras = async () => {
    try {
      const response = await api.get(`/assets/workstations/${id}/impresoras`);
      setImpresoras(response.data);
    } catch (error) {
      console.error('Error fetching impresoras:', error);
    }
  };

  const fetchSeguridad = async () => {
    try {
      const response = await api.get(`/assets/workstations/${id}/seguridad`);
      setSeguridad(response.data);
    } catch (error) {
      console.error('Error fetching seguridad:', error);
    }
  };

  const fetchShares = async () => {
    try {
      const response = await api.get(`/assets/workstations/${id}/shares`);
      setShares(response.data);
    } catch (error) {
      console.error('Error fetching shares:', error);
    }
  };

  const fetchLocaladmin = async () => {
    try {
      const response = await api.get(`/assets/workstations/${id}/localadmin`);
      setLocaladmin(response.data);
    } catch (error) {
      console.error('Error fetching localadmin:', error);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/workstations/${id}`);
      toast.success('Workstation eliminada exitosamente');
      navigate('/micro/workstations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!workstation) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Workstation no encontrada</p>
          <Link to="/micro/workstations" className="text-primary hover:underline mt-2 block">
            Volver al listado
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/micro/workstations">
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="page-title flex items-center gap-3">
                <Monitor className="h-6 w-6 text-primary" />
                {workstation.computername}
              </h1>
              <p className="page-subtitle">{hardware?.type || 'Desktop'} - {workstation.modelo || workstation.computername}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(() => {
              const status = getAgentStatus(agente);
              const badgeClasses = {
                active: 'badge badge-success',
                recent: 'badge badge-success',
                warning: 'badge badge-warning',
                inactive: 'badge badge-danger',
                not_installed: 'badge badge-warning',
                unknown: 'badge badge-secondary'
              };
              const badgeLabels = {
                active: 'Agente Activo',
                recent: 'Agente Activo',
                warning: 'Agente Inactivo',
                inactive: 'Agente Offline',
                not_installed: 'Sin Agente',
                unknown: 'Estado Desconocido'
              };
              return (
                <span className={badgeClasses[status.status]}>{badgeLabels[status.status]}</span>
              );
            })()}
            {canUpdate && (
              <Link to={`/micro/workstations`}>
                <Button variant="default" size="sm" className="gap-2" data-testid="edit-btn">
                  <Edit className="h-4 w-4" />
                  Editar
                </Button>
              </Link>
            )}
            {canDelete && (
              <Button 
                variant="destructive" 
                size="sm" 
                className="gap-2" 
                onClick={() => setDeleteModalOpen(true)}
                data-testid="delete-btn"
              >
                <Trash2 className="h-4 w-4" />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Status Card */}
        <Card className="nerve-card">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-8">
              {/* Agent Status */}
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Estado</p>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${
                    getAgentStatus(agente).color === 'green' ? 'bg-green-500' :
                    getAgentStatus(agente).color === 'blue' ? 'bg-blue-500' :
                    getAgentStatus(agente).color === 'yellow' ? 'bg-yellow-500' :
                    getAgentStatus(agente).color === 'red' ? 'bg-red-500' : 'bg-gray-500'
                  }`}></span>
                  <span className="text-lg">
                    {getAgentStatus(agente).status === 'active' || getAgentStatus(agente).status === 'recent' ? 'Online' :
                     getAgentStatus(agente).status === 'warning' ? 'Advertencia' :
                     getAgentStatus(agente).status === 'inactive' ? 'Offline' :
                     getAgentStatus(agente).status === 'not_installed' ? 'Sin agente' : 'Desconocido'}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">IP</p>
                <p className="font-mono text-lg text-primary">{hardware?.ip || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Modelo</p>
                <p className="text-lg font-medium">{workstation.modelo || hardware?.model || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">OS</p>
                <p className="text-lg">{getOSDisplayName(hardware?.osversion, hardware?.os)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">CPU / RAM</p>
                <p className="font-mono text-lg">
                  {hardware?.cores || '-'} cores / {hardware?.ram ? `${parseFloat(hardware.ram).toFixed(0)} GB` : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="nerve-card p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Monitor className="h-4 w-4 mr-1.5" />
              General
            </TabsTrigger>
            <TabsTrigger value="software" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Package className="h-4 w-4 mr-1.5" />
              Software
            </TabsTrigger>
            <TabsTrigger value="parches" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Wrench className="h-4 w-4 mr-1.5" />
              Parches
            </TabsTrigger>
            <TabsTrigger value="impresoras" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Printer className="h-4 w-4 mr-1.5" />
              Impresoras
            </TabsTrigger>
            <TabsTrigger value="seguridad" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Shield className="h-4 w-4 mr-1.5" />
              Seguridad
            </TabsTrigger>
            <TabsTrigger value="shares" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <FolderOpen className="h-4 w-4 mr-1.5" />
              Shares
            </TabsTrigger>
            <TabsTrigger value="localadmin" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <UserCog className="h-4 w-4 mr-1.5" />
              Local Admins
            </TabsTrigger>
            <TabsTrigger value="agente" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Activity className="h-4 w-4 mr-1.5" />
              Agente
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Información General */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Hash className="h-4 w-4 text-primary" />
                    Información General
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Hostname</span>
                    <span className="font-medium">{workstation.computername}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Nro. Serie</span>
                    <span className="font-mono text-sm">{workstation.serialnumber || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground w-24">Usuario</span>
                    <span className="font-medium">{workstation.assigned_user_name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Box className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Tipo</span>
                    <span>{hardware?.type || 'Desktop'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Factory className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Fabricante</span>
                    <span>{workstation.fabricante || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Modelo</span>
                    <span>{workstation.modelo || hardware?.model || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Red */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Network className="h-4 w-4 text-primary" />
                    Red
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">IP Principal</span>
                    <span className="font-mono text-sm text-primary">{hardware?.ip || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Wifi className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">MAC Address</span>
                    <span className="font-mono text-sm">{hardware?.mac || workstation.macaddress || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Hardware */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-primary" />
                    Hardware
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Cpu className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">CPU</span>
                    <span className="text-sm max-w-[200px] truncate" title={workstation.cpu}>
                      {workstation.cpu || '-'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CircuitBoard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Cores</span>
                    <span>{hardware?.cores || '-'} físicos / {hardware?.logicalcpu || '-'} lógicos</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MemoryStick className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">RAM</span>
                    <span className="font-medium">
                      {hardware?.ram || workstation.ram ? `${parseFloat(hardware?.ram || workstation.ram).toFixed(1)} GB` : '-'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <HardDrive className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Storage</span>
                    <span className="font-medium">
                      {hardware?.storage ? `${parseFloat(hardware.storage).toFixed(0)} GB` : '-'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Sistema */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-primary" />
                    Sistema
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">OS</span>
                    <span className="text-sm">{getOSDisplayName(hardware?.osversion, hardware?.os)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Box className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Edición</span>
                    <span className="text-sm">{hardware?.os_caption || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Build</span>
                    <span className="text-sm font-mono text-primary">{hardware?.osversion_full || hardware?.osversion || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Administración */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    Administración
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Gauge className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Estado</span>
                    <span>{workstation.status_name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Proveedor</span>
                    <span>{workstation.supplier_name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground w-24">Fecha Compra</span>
                    <span>{workstation.purchase_date || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Agente/Monitoreo */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    Monitoreo
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    {getAgentStatus(agente).color === 'green' || getAgentStatus(agente).color === 'blue' ? (
                      <CheckCircle className={`h-4 w-4 ${getAgentStatus(agente).color === 'green' ? 'text-green-500' : 'text-blue-500'}`} />
                    ) : getAgentStatus(agente).color === 'yellow' ? (
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                    ) : getAgentStatus(agente).color === 'red' ? (
                      <XCircle className="h-4 w-4 text-red-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="text-sm text-muted-foreground w-24">Agente</span>
                    <span className={`font-medium ${
                      getAgentStatus(agente).color === 'green' ? 'text-green-600' :
                      getAgentStatus(agente).color === 'blue' ? 'text-blue-600' :
                      getAgentStatus(agente).color === 'yellow' ? 'text-yellow-600' :
                      getAgentStatus(agente).color === 'red' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {agente ? `Instalado - ${getAgentStatus(agente).label}` : getAgentStatus(agente).label}
                    </span>
                  </div>
                  {agente && (
                    <>
                      <div className="flex items-center gap-3">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground w-24">Versión</span>
                        <span>{agente.version_agente || '-'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground w-24">Últ. Reporte</span>
                        <span className="text-sm">{agente.ultimo_reporte || '-'}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {workstation.comments && (
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold">Comentarios</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">{workstation.comments}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Software Tab */}
          <TabsContent value="software">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" />
                    Software Instalado ({software.total})
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar software o proveedor..."
                      value={softwareSearch}
                      onChange={(e) => { setSoftwareSearch(e.target.value); setSoftware(prev => ({ ...prev, page: 1 })); }}
                      className="pl-8 h-8 w-56 text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {software.items.length > 0 ? (
                  <>
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setSoftwareSort)('name'); setSoftware(prev => ({ ...prev, page: 1 })); }}
                          >
                            <div className="flex items-center gap-1">
                              NOMBRE
                              <SortIcon field="name" sortConfig={softwareSort} />
                            </div>
                          </th>
                          <th>VERSION</th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setSoftwareSort)('providername'); setSoftware(prev => ({ ...prev, page: 1 })); }}
                          >
                            <div className="flex items-center gap-1">
                              PROVEEDOR
                              <SortIcon field="providername" sortConfig={softwareSort} />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {software.items.map((sw, idx) => (
                          <tr key={idx}>
                            <td className="font-medium">{sw.name}</td>
                            <td className="text-muted-foreground text-sm font-mono">{sw.version || '-'}</td>
                            <td className="text-muted-foreground text-sm">{sw.providername || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {software.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                        <span className="text-sm text-muted-foreground">
                          {(software.page - 1) * 25 + 1} - {Math.min(software.page * 25, software.total)} de {software.total}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setSoftware(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={software.page === 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm">{software.page} / {software.pages}</span>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setSoftware(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={software.page === software.pages}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {softwareSearch ? 'Sin resultados para el filtro' : 'No hay software registrado'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parches Tab */}
          <TabsContent value="parches">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-primary" />
                    Parches Instalados ({parches.total})
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar KB o descripción..."
                      value={parchesSearch}
                      onChange={(e) => { setParchesSearch(e.target.value); setParches(prev => ({ ...prev, page: 1 })); }}
                      className="pl-8 h-8 w-56 text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {parches.items.length > 0 ? (
                  <>
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th>TIPO</th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setParchesSort)('id_parche'); setParches(prev => ({ ...prev, page: 1 })); }}
                          >
                            <div className="flex items-center gap-1">
                              ID PARCHE
                              <SortIcon field="id_parche" sortConfig={parchesSort} />
                            </div>
                          </th>
                          <th>TITULO / DESCRIPCION</th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => { createSortHandler(setParchesSort)('fecha_instalacion'); setParches(prev => ({ ...prev, page: 1 })); }}
                          >
                            <div className="flex items-center gap-1">
                              FECHA INSTALACION
                              <SortIcon field="fecha_instalacion" sortConfig={parchesSort} />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {parches.items.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <span className={`badge ${
                                p.kb_tipo?.includes('Security') ? 'badge-error' :
                                p.kb_tipo?.includes('Cumulative') ? 'badge-warning' :
                                'badge-info'
                              }`}>
                                {p.kb_tipo || p.tipo_parche || 'Update'}
                              </span>
                            </td>
                            <td className="font-mono text-sm text-primary">{p.id_parche}</td>
                            <td className="text-muted-foreground text-sm max-w-[300px] truncate" title={p.kb_titulo || p.descripcion}>
                              {p.kb_titulo || p.descripcion || '-'}
                            </td>
                            <td className="text-muted-foreground text-sm">{p.fecha_instalacion || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parches.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                        <span className="text-sm text-muted-foreground">
                          {(parches.page - 1) * 25 + 1} - {Math.min(parches.page * 25, parches.total)} de {parches.total}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setParches(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={parches.page === 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm">{parches.page} / {parches.pages}</span>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setParches(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={parches.page === parches.pages}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {parchesSearch ? 'Sin resultados para el filtro' : 'No hay parches registrados'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Impresoras Tab */}
          <TabsContent value="impresoras">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Printer className="h-4 w-4 text-primary" />
                    Impresoras ({getFilteredImpresoras().length})
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar impresora..."
                      value={impresorasSearch}
                      onChange={(e) => setImpresorasSearch(e.target.value)}
                      className="pl-8 h-8 w-48 text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {getFilteredImpresoras().length > 0 ? (
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => createSortHandler(setImpresorasSort)('printer')}
                        >
                          <div className="flex items-center gap-1">
                            IMPRESORA
                            <SortIcon field="printer" sortConfig={impresorasSort} />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => createSortHandler(setImpresorasSort)('drivername')}
                        >
                          <div className="flex items-center gap-1">
                            DRIVER
                            <SortIcon field="drivername" sortConfig={impresorasSort} />
                          </div>
                        </th>
                        <th>PUERTO</th>
                        <th>COMPARTIDA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredImpresoras().map((imp, idx) => (
                        <tr key={idx}>
                          <td className="font-medium">{imp.printer}</td>
                          <td className="text-muted-foreground text-sm">{imp.drivername || '-'}</td>
                          <td className="text-muted-foreground text-sm font-mono">{imp.port || '-'}</td>
                          <td>
                            {imp.shared === 'True' ? (
                              <span className="badge badge-success">Sí</span>
                            ) : (
                              <span className="badge badge-outline">No</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8">
                    <Printer className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {impresorasSearch ? 'Sin resultados para el filtro' : 'No hay impresoras registradas'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Seguridad Tab */}
          <TabsContent value="seguridad">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    Estado de Seguridad ({getFilteredSeguridad().length})
                  </CardTitle>
                  <Select value={seguridadFilter} onValueChange={setSeguridadFilter}>
                    <SelectTrigger className="h-8 w-40 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="antivirus">Antivirus</SelectItem>
                      <SelectItem value="firewall">Firewall</SelectItem>
                      <SelectItem value="bitlocker">BitLocker</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {getFilteredSeguridad().length > 0 ? (
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th>TIPO</th>
                        <th>PRODUCTO</th>
                        <th>VERSION</th>
                        <th>ESTADO</th>
                        <th>ACTUALIZADO</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredSeguridad().map((sec, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className="badge badge-info">{sec.tipo_seguridad}</span>
                          </td>
                          <td className="font-medium">{sec.nombre_producto || '-'}</td>
                          <td className="text-muted-foreground text-sm">{sec.version || '-'}</td>
                          <td>
                            {sec.estado?.toLowerCase().includes('on') || sec.proteccion_activada === 'True' ? (
                              <span className="badge badge-success">Activo</span>
                            ) : (
                              <span className="badge badge-error">Inactivo</span>
                            )}
                          </td>
                          <td className="text-muted-foreground text-sm">{sec.actualizado || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {seguridadFilter !== 'all' ? 'Sin resultados para el filtro' : 'No hay información de seguridad'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shares Tab */}
          <TabsContent value="shares">
            <Card className="card-subtle">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FolderOpen className="h-4 w-4 text-primary" />
                  Carpetas Compartidas ({shares.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="table-subtle">
                  <thead>
                    <tr>
                      <th>NOMBRE</th>
                      <th>TIPO</th>
                      <th>COMENTARIO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shares.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center py-8 text-muted-foreground">
                          No hay carpetas compartidas
                        </td>
                      </tr>
                    ) : shares.map((sh, idx) => (
                      <tr key={idx}>
                        <td className="font-medium font-mono">{sh.sharename}</td>
                        <td className="text-muted-foreground text-sm">{sh.type || '-'}</td>
                        <td className="text-muted-foreground text-sm">{sh.comment || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Local Admins Tab */}
          <TabsContent value="localadmin">
            <Card className="card-subtle">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-primary" />
                  Administradores Locales ({localadmin.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="table-subtle">
                  <thead>
                    <tr>
                      <th>DOMINIO</th>
                      <th>USUARIO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localadmin.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center py-8 text-muted-foreground">
                          No hay administradores locales registrados
                        </td>
                      </tr>
                    ) : localadmin.map((la, idx) => (
                      <tr key={idx}>
                        <td className="text-muted-foreground">{la.domain || 'LOCAL'}</td>
                        <td className="font-medium">{la.user}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agente Tab */}
          <TabsContent value="agente">
            <Card className="card-subtle">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  Estado del Agente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {agente ? (
                  <>
                    <div className={`mb-4 p-3 rounded-lg border ${
                      getAgentStatus(agente).color === 'green' ? 'bg-green-100 border-green-300' :
                      getAgentStatus(agente).color === 'blue' ? 'bg-blue-100 border-blue-300' :
                      getAgentStatus(agente).color === 'yellow' ? 'bg-yellow-100 border-yellow-300' :
                      getAgentStatus(agente).color === 'red' ? 'bg-red-100 border-red-300' : 'bg-gray-100 border-gray-300'
                    }`}>
                      <div className="flex items-center gap-2">
                        {getAgentStatus(agente).color === 'green' || getAgentStatus(agente).color === 'blue' ? (
                          <CheckCircle className={`h-5 w-5 ${getAgentStatus(agente).color === 'green' ? 'text-green-700' : 'text-blue-700'}`} />
                        ) : getAgentStatus(agente).color === 'yellow' ? (
                          <AlertCircle className="h-5 w-5 text-yellow-700" />
                        ) : getAgentStatus(agente).color === 'red' ? (
                          <XCircle className="h-5 w-5 text-red-700" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-gray-700" />
                        )}
                        <span className={`font-medium ${
                          getAgentStatus(agente).color === 'green' ? 'text-green-700' :
                          getAgentStatus(agente).color === 'blue' ? 'text-blue-700' :
                          getAgentStatus(agente).color === 'yellow' ? 'text-yellow-700' :
                          getAgentStatus(agente).color === 'red' ? 'text-red-700' : 'text-gray-700'
                        }`}>{getAgentStatus(agente).label}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-muted-foreground text-sm">Nombre PC</span>
                        <p className="font-medium">{agente.nombre_pc}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-sm">Versión Agente</span>
                        <p className="font-medium">{agente.version_agente || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-sm">Último Reporte</span>
                        <p className="font-medium">{agente.ultimo_reporte || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-sm">Última Actualización</span>
                        <p className="font-medium">{agente.ultima_actualizacion || '-'}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                    <p className="text-muted-foreground">Este equipo no tiene agente instalado</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                ¿Está seguro que desea eliminar la workstation <span className="font-semibold text-foreground">{workstation?.computername}</span>?
              </p>
              <p className="text-sm text-destructive mt-2">
                Esta acción eliminará también todos los registros relacionados y no se puede deshacer.
              </p>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WorkstationDetail;
